import { Content } from 'antd/es/layout/layout';
import './ContentComponent.scss';
import { Analytics } from '@/pages/ReceptionistDashboard/Analytics';
import CallLogs from '../LogsComponent/CallLogs/CallLogs';
import TextLogs from '../LogsComponent/TextLogs/TextLogs';
import VoiceMailLogs from '../LogsComponent/VoiceMailLogs/VoiceMailLogs';
import CrmDashboard from '../CrmDashboard/CrmDashboard';
import VoiceLibrary from '../VoiceLibrary/voiceLibrary';
import { TextingPlatform } from '../TextingPlatForm/index';
import IntakeForms from '../IntakeForms/IntakeForms';
import { ReceptionistDashboardSiderItem } from 'src/enums/EReceptionistDashboardSiderItem';
import { PhoneNumberSettings } from '../TestItOut/v2/PhoneNumberSettings';
import ExploreFeaturesPage from '../ExploreFeatures';
import { Playground } from '../Playground';
import { Embeddables } from '../Embeddables';
import { PRStatsPage } from '@/pages/PRStats';
import TrialOverlay from '@/components/TrialOverlay';
import ResellerPlans from '../ResellerPlans/ResellerPlans';
import { ManageNotifications } from '../Overview/ManageNotifications/ManageNotifications';
import TestReceptionist from '../TestReceptionist/TestReceptionist';
import { UserAccount } from '@/types/TAccounts';
import { useEffect } from 'react';
import AICustomizationStep from '../TestReceptionist/AICustomizationStep/AICustomizationStep';
import { FeatureFlag } from 'src/enums/EFeatureFlags';
import { useFeatureFlag } from 'src/Hooks/useFeatureFlag';
import { getLoggedInUser, getSelectedAgent } from '@/store/account/selector';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import { useLocation, useNavigate } from 'react-router-dom';
import ApiWorkflowTemplate from '../ApiWorkflowTemplate/ApiWorkflowTemplate';
import ManagePassword from '@/pages/UserDashboard/Setting/SettingMenu/ManagePassword/ManagePassword';
import ManageCredits from '@/pages/UserDashboard/Setting/SettingMenu/ManageCredits/ManageCredits';
import AccountSettings from '@/pages/UserDashboard/Setting/SettingMenu/AccountSettings/AccountSettings';
import ManageUsage from '@/pages/UserDashboard/Setting/SettingMenu/ManageSubscription/ManageUsage';
import ManageSubscription from '@/pages/UserDashboard/Setting/SettingMenu/ManageSubscription/ManageSubscription';
import Dashboard from '@/pages/UserDashboard/Dashboard/Dashboard';
import GeneralSettings from '../GeneralSettings/GeneralSettings';
import Inbox from '../LogsComponent/Inbox/inbox';
import { ContactSupport } from '../ContactSupport';
interface ContentComponentProps {
	currentPage: string;
	setOpened: (open: boolean) => void;
	setOpenedOnboarding: (open: boolean) => void;
	debrand: boolean;
	apiWorkflowRef: React.RefObject<HTMLDivElement>;
	gcalRef: React.RefObject<HTMLDivElement>;
	intakeRef: React.RefObject<HTMLDivElement>;
	currentStep: number;
	setCurrentStep: (step: any) => void;
	openDrawer?: () => void;
	isReceptionistLoading?: boolean;
	completedFeatures: number[];
	setCompletedFeatures?: any;
	loggedInUser: UserAccount;
}

const ContentComponent: React.FC<ContentComponentProps> = ({
	currentPage,
	debrand,
	setOpenedOnboarding,
}) => {
	const selectedAgent = useAppSelector(getSelectedAgent);
	// Add feature flag check
	const isGohighlevelIntegrationEnabled = useFeatureFlag(FeatureFlag.GOHIGHLEVEL_INTEGRATION);
	const loggedInUser = useAppSelector(getLoggedInUser);

	// Add location and navigate for redirection
	const navigate = useNavigate();
	const location = useLocation();

	// Check if we're on the integrations page without the feature flag enabled
	useEffect(() => {
		if (
			location.pathname === '/app-integrations' &&
			!isGohighlevelIntegrationEnabled &&
			currentPage === ReceptionistDashboardSiderItem.APPINTEGRATIONS
		) {
			// Redirect to home if integration feature is disabled
			navigate('/');
		}
	}, [location.pathname, isGohighlevelIntegrationEnabled, navigate, currentPage]);

	// Check if current page should display the trial overlay
	const shouldShowTrialOverlay = () => {
		// Don't show overlay if user is not in demo mode
		if (
			(selectedAgent?.type === 'Inbound' &&
				loggedInUser?.inboundSubscriptionStatus &&
				loggedInUser?.inboundSubscriptionStatus !== 'cancelled') ||
			(selectedAgent?.type === 'Outbound' &&
				loggedInUser?.outboundSubscriptionStatus &&
				loggedInUser?.outboundSubscriptionStatus !== 'cancelled') ||
			loggedInUser?.resellerClient
		) {
			return false;
		}

		// Only show overlay on premium feature pages that require a subscription
		const premiumFeaturePages = [
			ReceptionistDashboardSiderItem.TEXTING_AGENT,
			ReceptionistDashboardSiderItem.CRM,
			ReceptionistDashboardSiderItem.ANALYTICS,
			ReceptionistDashboardSiderItem.EMBEDDABLES,
			// Add any new premium features here
		];

		// Only show overlay on premium feature pages
		return premiumFeaturePages.includes(currentPage as ReceptionistDashboardSiderItem);
	};

	// Add/remove no-scroll class to body when overlay is shown/hidden
	useEffect(() => {
		const showOverlay = shouldShowTrialOverlay();

		if (showOverlay) {
			document.body.classList.add('content-no-scroll');
		} else {
			document.body.classList.remove('content-no-scroll');
		}

		return () => {
			// Clean up when component unmounts
			document.body.classList.remove('content-no-scroll');
		};
	}, [currentPage, loggedInUser?.inboundDemo, loggedInUser?.outboundDemo]);

	const inboundAgentContent = (currentPage: string) => {
		// Fall back to the switch based on currentPage
		switch (currentPage) {
			case ReceptionistDashboardSiderItem.SETUP_INSTRUCTIONS:
				return <PhoneNumberSettings setOpenedOnboarding={setOpenedOnboarding} debrand={true} />;
			case ReceptionistDashboardSiderItem.GET_STARTED:
				return <TestReceptionist />;
			case ReceptionistDashboardSiderItem.HIRE_AI_RECEPTIONIST:
				return <TestReceptionist />;
			case ReceptionistDashboardSiderItem.RECEPTIONIST:
				return <AICustomizationStep onTest={() => {}} showTestBtn={false} debrand={debrand} />;
			case ReceptionistDashboardSiderItem.NOTIFICATIONS:
				return <ManageNotifications />;
			case ReceptionistDashboardSiderItem.TEST_CALL:
				return <Playground debrand={debrand} />;
			case ReceptionistDashboardSiderItem.APPINTEGRATIONS:
				return isGohighlevelIntegrationEnabled ? <ApiWorkflowTemplate /> : null;
			case ReceptionistDashboardSiderItem.EMBEDDABLES:
				return <Embeddables />;
			case ReceptionistDashboardSiderItem.ANALYTICS:
				return <Analytics debrand={debrand} />;
			case ReceptionistDashboardSiderItem.PR_STATS:
				return <PRStatsPage />;
			case ReceptionistDashboardSiderItem.VOICE_LIBRARY:
				return <VoiceLibrary debrand={debrand} />;
			case ReceptionistDashboardSiderItem.TEXTING_AGENT:
				return <TextingPlatform debrand={debrand} isNoviUser={false} />;
			case ReceptionistDashboardSiderItem.INTAKE_FORMS:
				return <IntakeForms />;
			case ReceptionistDashboardSiderItem.CALL_LOGS:
				return <CallLogs debrand={debrand} isNoviUser={false} />;
			case ReceptionistDashboardSiderItem.ALL_LOGS:
				return <Inbox debrand={debrand} isNoviUser={false} />;
			case ReceptionistDashboardSiderItem.TEXT_LOGS:
				return <TextLogs />;
			case ReceptionistDashboardSiderItem.VOICE_MAIL_LOGS:
				return <VoiceMailLogs />;
			case ReceptionistDashboardSiderItem.CRM:
				return <CrmDashboard isNoviUser={false} />;
			case ReceptionistDashboardSiderItem.EXPLORE_FEATURES:
				return <ExploreFeaturesPage />;
			case ReceptionistDashboardSiderItem.RESELLER_PLAN:
				return <ResellerPlans />;
			case ReceptionistDashboardSiderItem.PASSWORD:
				return <ManagePassword />;
			case ReceptionistDashboardSiderItem.CREDITS:
				return <ManageCredits />;
			case ReceptionistDashboardSiderItem.SETTINGS:
				return <AccountSettings isLoading={false} />;
			case ReceptionistDashboardSiderItem.USAGE:
				return <ManageUsage />;
			case ReceptionistDashboardSiderItem.SUBSCRIPTION:
				return <ManageSubscription />;
			case ReceptionistDashboardSiderItem.DASHBOARD:
				return <Dashboard />;
			case ReceptionistDashboardSiderItem.GENERAL_SETTINGS:
				return <GeneralSettings isLoading={false} />;
			case ReceptionistDashboardSiderItem.CONTACT_SUPPORT:
				return <ContactSupport />;
			default:
				break;
		}
	};

	const renderContent = () => {
		return inboundAgentContent(currentPage);
	};

	const baseClass = 'receptionist-content-component';
	const showOverlay = shouldShowTrialOverlay();

	return (
		<Content className={baseClass}>
			<div className={`${baseClass}_content relative ${showOverlay ? 'overlay-active' : ''}`}>
				{renderContent()}

				{/* Trial overlay that only shows on specific premium feature pages and only covers the content area */}
				<TrialOverlay
					show={showOverlay}
					title="Unlock Full AI Features"
					description="Start your free trial today to access all premium features and experience the full potential of My AI Front Desk."
					buttonText="Start 7-Day Free Trial"
				/>
			</div>
		</Content>
	);
};

export default ContentComponent;
